import { useMemo } from "react";
import type { ImmutableObject } from "@/lib/immutable";
import { storefrontSdk } from "@/storefrontsdk";
import type { WebApiModulesIntegrationsStorefrontStorefrontSettings } from "../lib/storefront-api";
import { createStore, type Store, useStore } from "./store";
import { type AppConfigState, useAppConfigState } from "./useAppConfigState";
import { type SiteLayoutState, useSiteLayoutState } from "./useSiteLayoutState";

export class StorefrontSettingsModel {
	settings?: WebApiModulesIntegrationsStorefrontStorefrontSettings;
	preventLoading: boolean = false;
	isLoading: boolean = false;
}

const settingsStateStore = createStore<StorefrontSettingsModel>(
	new StorefrontSettingsModel(),
);

async function waitUntilAsync(condition: () => boolean, time: number = 100) {
	while (!condition()) {
		await new Promise((resolve) => setTimeout(resolve, time));
	}
}

export class StorefrontSettingsState {
	private settingsState: Store<StorefrontSettingsModel>;
	private appConfigState: AppConfigState;
	private siteLayoutState: SiteLayoutState;

	constructor(
		settingsState: Store<StorefrontSettingsModel>,
		appConfigState: AppConfigState,
		siteLayoutState: SiteLayoutState,
	) {
		this.settingsState = settingsState;
		this.appConfigState = appConfigState;
		this.siteLayoutState = siteLayoutState;
	}

	get(): ImmutableObject<StorefrontSettingsModel> {
		return this.settingsState.getState();
	}

	async loadAsync() {
		try {
			if (
				this.appConfigState.get().appConfig &&
				/*!this.settingsState.get().settings &&*/ !this.settingsState.getState()
					.isLoading &&
				!this.settingsState.getState().preventLoading
			) {
				this.settingsState.setState((prev) => ({ ...prev, isLoading: true }));
				const getSettingsResponse =
					await storefrontSdk.getStorefrontSettingsAsync();
				const settings = getSettingsResponse.data;
				this.settingsState.setState((prev) => ({ ...prev, settings }));
			} else if (this.settingsState.getState().isLoading) {
				await waitUntilAsync(() => !this.settingsState.getState().isLoading);
			}
		} catch (error) {
			this.settingsState.setState((prev) => ({
				...prev,
				preventLoading: true,
			}));
			this.siteLayoutState.showError(error);
		} finally {
			if (this.settingsState.getState().isLoading) {
				this.settingsState.setState((prev) => ({ ...prev, isLoading: false }));
			}
		}
	}
}

export function useStorefrontSettingsState(): StorefrontSettingsState {
	const appConfigState = useAppConfigState();
	const siteLayoutState = useSiteLayoutState();
	useStore(settingsStateStore);
	return useMemo(
		() =>
			new StorefrontSettingsState(
				settingsStateStore,
				appConfigState,
				siteLayoutState,
			),
		[appConfigState, siteLayoutState],
	);
}
